import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import API from '../api/Api';
import User from '../models/User';
import Loading from './Loading';
import { fetchTargetInstitution } from './Request/requestSlice';
import { useAppDispatch } from '../shared/redux/store';

type RequestType = 'internal' | 'external' | 'undefined';

interface AppContextInterface {
  isLoading: boolean
  login: () => void
  user: User
  haveWeTriedToAuthenticate: boolean,
  logout: () => void,
  requestType: RequestType,
  setRequestType: (value: RequestType) => void;
}
const AppState: AppContextInterface = {
  isLoading: false,
  login: () => { },
  user: { name: '', authorities: [], valid: false },
  haveWeTriedToAuthenticate: false,
  logout: () => { },
  requestType: 'undefined',
  setRequestType: () => {},
};

export const AppContext = createContext<AppContextInterface>(AppState);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AppContextInterface>(AppState);
  const dispatch = useAppDispatch()

  const login = () => {
    startLoading();
    setTimeout(() => {
      API.goToLogin();
    }, 250);
  };

  const logout = () => {
    setState((prev) => ({
      ...prev,
      user: AppState.user,
      haveWeTriedToAuthenticate: true,
      authorized: false
    }));
  }

  const startLoading = () => {
    setState((prev) => ({
      ...prev,
      isLoading: true
    }));
  };

  const stopLoading = () => {
    setState((prev) => ({
      ...prev,
      isLoading: false
    }));
  };

  const setRequestType = (value: RequestType) => {
    setState(prev => ({
      ...prev,
      requestType: value
    }));
  };

  const init = async () => {
    const app = await API.initApp();
    setState((prev) => ({
      ...prev,
      user: app.user?.authorities ? { ...app.user, valid: true } : AppState.user,
      haveWeTriedToAuthenticate: true
    }));
    stopLoading();
  };

  useEffect(() => {
    init().catch(err => console.log(err));
    // fetch target institution that is required all over the project
    dispatch(fetchTargetInstitution()).catch(e => console.log(e));
  }, []);

  const contextValues = useMemo(() => ({
    ...state,
    login,
    logout, 
    setRequestType
  }), [state, login, logout]);

  return (
    <AppContext.Provider value={contextValues}>
      {state.isLoading && <Loading isLoading={state.isLoading} />}
      {!state.isLoading && children && children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  return useContext(AppContext);
};

AppProvider.propTypes = {
  children: PropTypes.element
};
